import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle, XCircle, User, Mail, Calendar, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { idToken, user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // TEMPORARY: Allow bypass for testing with ?dev=true
    const urlParams = new URLSearchParams(window.location.search);
    const isDev = urlParams.get('dev') === 'true';
    
    if (isDev) {
      console.log('⚠️ DEV MODE: Bypassing admin authentication');
      setIsAdmin(true);
      setIsLoading(false);
      return;
    }
    
    // Redirect to admin login if not authenticated
    if (!isAuthenticated) {
      window.location.href = '/admin/login';
      return;
    }
    checkAdminStatus();
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAdmin) {
      if (activeTab === 'users') {
        fetchUsers();
      } else {
        fetchContacts();
      }
    }
  }, [activeTab, isAdmin]);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/admin/check`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      setIsAdmin(response.data.is_admin);
      if (!response.data.is_admin) {
        toast.error('Access denied. Admin privileges required.');
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      toast.error('Access denied');
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  const getHeaders = () => {
    // TEMPORARY: Skip auth header in dev mode
    const urlParams = new URLSearchParams(window.location.search);
    const isDev = urlParams.get('dev') === 'true';
    
    if (isDev) {
      return {};
    }
    
    return {
      Authorization: `Bearer ${idToken}`
    };
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/admin/users`, {
        headers: getHeaders()
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contact`, {
        headers: getHeaders()
      });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contact submissions');
    }
  };

  const approveUser = async (userId) => {
    try {
      await axios.patch(`${API}/auth/admin/users/${userId}/approve`, {}, {
        headers: getHeaders()
      });
      toast.success('User approved successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error('Failed to approve user');
    }
  };

  const rejectUser = async (userId) => {
    try {
      await axios.patch(`${API}/auth/admin/users/${userId}/reject`, {}, {
        headers: getHeaders()
      });
      toast.success('User rejected');
      fetchUsers();
    } catch (error) {
      console.error('Error rejecting user:', error);
      toast.error('Failed to reject user');
    }
  };

  if (isLoading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Checking admin access...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-container">
        <div className="admin-access-denied">
          <XCircle size={64} />
          <h2>Admin Access Only</h2>
          <p>
            This area is restricted to authorized administrators only.
            {!user && ' You must be logged in with an admin account to access this dashboard.'}
          </p>
          <Button onClick={() => window.location.href = '/'} className="back-home-btn">
            <ArrowLeft size={18} />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const pendingUsers = users.filter(u => !u.approved);
  const approvedUsers = users.filter(u => u.approved);

  return (
    <div className="admin-container">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <button onClick={() => window.location.href = '/'} className="back-button">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>
          <div>
            <h1>Admin Dashboard</h1>
            <p>Manage users and view contact submissions</p>
          </div>
        </div>
        <div className="admin-user-info">
          <span className="admin-email">{user?.email}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <User size={18} />
          Users ({users.length})
        </button>
        <button 
          className={`admin-tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          <Mail size={18} />
          Contact Submissions ({contacts.length})
        </button>
      </div>

      {/* Content */}
      <div className="admin-content">
        {activeTab === 'users' ? (
          <>
            {/* Pending Users */}
            {pendingUsers.length > 0 && (
              <div className="admin-section">
                <h2 className="section-title">Pending Approval ({pendingUsers.length})</h2>
                <div className="users-grid">
                  {pendingUsers.map(user => (
                    <Card key={user.user_id} className="user-card pending">
                      <CardHeader>
                        <CardTitle className="user-card-title">
                          <div className="user-avatar">
                            <User size={24} />
                          </div>
                          <div>
                            <div className="user-name">{user.username}</div>
                            <div className="user-email">{user.email}</div>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="user-info">
                          <div className="info-item">
                            <Calendar size={14} />
                            <span>Registered: {new Date(user.createdAt).toLocaleDateString()}</span>
                          </div>
                          {user.lastLoginAt && (
                            <div className="info-item">
                              <Calendar size={14} />
                              <span>Last login: {new Date(user.lastLoginAt).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                        <div className="user-actions">
                          <Button 
                            onClick={() => approveUser(user.user_id)} 
                            className="approve-btn"
                          >
                            <CheckCircle size={16} />
                            Approve
                          </Button>
                          <Button 
                            onClick={() => rejectUser(user.user_id)} 
                            className="reject-btn"
                          >
                            <XCircle size={16} />
                            Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Approved Users */}
            <div className="admin-section">
              <h2 className="section-title">Approved Users ({approvedUsers.length})</h2>
              {approvedUsers.length === 0 ? (
                <p className="empty-message">No approved users yet.</p>
              ) : (
                <div className="users-grid">
                  {approvedUsers.map(user => (
                    <Card key={user.user_id} className="user-card approved">
                      <CardHeader>
                        <CardTitle className="user-card-title">
                          <div className="user-avatar">
                            <User size={24} />
                          </div>
                          <div>
                            <div className="user-name">{user.username}</div>
                            <div className="user-email">{user.email}</div>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="user-info">
                          <div className="info-item">
                            <Calendar size={14} />
                            <span>Registered: {new Date(user.createdAt).toLocaleDateString()}</span>
                          </div>
                          {user.lastLoginAt && (
                            <div className="info-item">
                              <Calendar size={14} />
                              <span>Last login: {new Date(user.lastLoginAt).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                        <div className="user-status-badge approved">
                          <CheckCircle size={14} />
                          Approved
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="admin-section">
            <h2 className="section-title">Contact Form Submissions ({contacts.length})</h2>
            {contacts.length === 0 ? (
              <p className="empty-message">No contact submissions yet.</p>
            ) : (
              <div className="contacts-list">
                {contacts.map(contact => (
                  <Card key={contact.id} className="contact-card">
                    <CardHeader>
                      <CardTitle className="contact-title">
                        <div>
                          <div className="contact-name">{contact.name}</div>
                          <div className="contact-email">{contact.email}</div>
                        </div>
                        <div className="contact-date">
                          {new Date(contact.createdAt).toLocaleDateString()}
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {contact.company && (
                        <div className="contact-company">
                          <strong>Company:</strong> {contact.company}
                        </div>
                      )}
                      <div className="contact-message">
                        <strong>Message:</strong>
                        <p>{contact.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
